<template>
    <component
        :is="FormElementComponent"
        :id="id"
        :form-element-component="SelfComponent"
        :currency="currency"
        :custom-component-input="customComponents"
        :form="form"
        :value="value"
        :depth="depth"
        :description="description"
        :evaluation-errors="evaluationErrors"
        :start-index="startIndex"
        :no-object-index="noObjectIndex"
        v-bind="listenerAttrs"
    />
</template>

<script lang="ts">
    import { markRaw, type Component as VueComponent } from 'vue';

    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import JsonForm, { JsonFormType } from '@evidentid/json-schema/interfaces/JsonForm';
    import { pickListenerAttrs } from '@evidentid/vue-commons/utils/attrsListeners';

    import { FieldEvaluationResultError } from '@/modules/decisioning-criteria/types';
    import JsonSchemaArrayView from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaArrayView.vue';
    import JsonSchemaBooleanView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaBooleanView.vue';
    import JsonSchemaNumberView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaNumberView.vue';
    import JsonSchemaObjectView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaObjectView.vue';
    import JsonSchemaStringView
        from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaStringView.vue';
    import JsonSchemaTextView from '@/modules/entity-details/components/JsonSchemaView/elements/JsonSchemaTextView.vue';

    const elementByType: Record<JsonFormType, VueComponent> = {
        [JsonFormType.array]: JsonSchemaArrayView,
        [JsonFormType.string]: JsonSchemaStringView,
        [JsonFormType.number]: JsonSchemaNumberView,
        [JsonFormType.boolean]: JsonSchemaBooleanView,
        [JsonFormType.object]: JsonSchemaObjectView,
        [JsonFormType.const]: JsonSchemaTextView,
        [JsonFormType.enum]: JsonSchemaTextView,
    };

    @Component({
        inheritAttrs: false,
    })
    export default class JsonSchemaViewElement extends Vue {
        // markRaw: a class field becomes reactive data, and wrapping a component
        // definition in a proxy makes Vue warn about the needless overhead every time
        // it resolves this `<component :is>`.
        private SelfComponent = markRaw(JsonSchemaViewElement);

        @Prop(Object)
        private form!: JsonForm;

        @Prop()
        private value!: any;

        @Prop({ type: String, default: '' })
        private description!: string;

        @Prop({ type: Number, default: 0 })
        private depth!: number;

        @Prop(String)
        private id!: string;

        @Prop({ type: Object, default: () => ({}) })
        private customComponents!: Partial<Record<JsonFormType, VueComponent>>;

        @Prop({ type: Object, default: () => ({}) })
        private evaluationErrors!: Record<string, FieldEvaluationResultError>;

        @Prop({ type: String, default: 'USD' })
        private currency!: string;

        @Prop({ type: Number, default: 1 })
        private startIndex!: number;

        @Prop({ type: Boolean, default: false })
        private noObjectIndex!: boolean;

        private get FormElementComponent() {
            return this.customComponents[this.form.type] || elementByType[this.form.type];
        }

        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): this component is `inheritAttrs:
        // false` and never spreads raw `$attrs` -- only declared props + listeners are
        // forwarded to the resolved element. Vue 3 merges listeners into `$attrs`, so
        // pick just the `onXxx` keys back out instead of a blanket `v-bind="$attrs"`.
        private get listenerAttrs(): Record<string, unknown> {
            return pickListenerAttrs(this.$attrs);
        }
    }
</script>
